/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, RefObject } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { ElementRef } from 'preact/compat';
import { colorsAreDifferent, type ColorValue } from '#utils/UNSAFE_color';
import { ColorPicker } from '#UNSAFE_ColorPicker';
import { ColorSwatch } from '#UNSAFE_ColorSwatch';
import { CloseDetail, InputColorPickerDropdown } from './InputColorPickerDropdown';
import { useInputColorState } from './useInputColorState';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { useDownToggle } from '#hooks/UNSAFE_useDownToggle';
import { InputColorPublicProps as InputColorProps } from './InputColor.types';

type SwatchHandle = ElementRef<typeof ColorSwatch>;

type ColorPickerProps = ComponentProps<typeof ColorPicker>;
type ColorSwatchProps = ComponentProps<typeof ColorSwatch>;
type DropdownProps = ComponentProps<typeof InputColorPickerDropdown>;

type Props = {
  onCommit: InputColorProps['onCommit'];
  onInput: InputColorProps['onInput'];
  value: InputColorProps['value'];
};

/**
 * Hook that provides handlers and other functionality for InputColor.
 */
const useInputColor = ({ onCommit, onInput, value: currentValue }: Props) => {
  // Whether the dropdown is open.
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Ref to the swatch icon button.
  const iconRef: RefObject<SwatchHandle> = useRef<SwatchHandle>(null);

  // Ref to the text input which holds color value
  const colorRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

  // Keep track of whether the dropdown was just dismissed from an outside mousedown event.
  const outsideMousedownRef = useRef<boolean>(false);
  const outsideMousedownTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  // Keep track of the last color values passed to onInput and onCommit.
  const lastCommittedColorRef = useRef<ColorValue | undefined>(currentValue);
  const lastColorRef = useRef<ColorValue | undefined>(currentValue);

  // Keep track of the last controlled value passed from the parent, so we can detect a programmatic change.
  const lastValueFromParentRef = useRef<ColorValue | undefined>(currentValue);

  // Keep track of whether a color was selected in the color picker.
  const colorSelectedRef = useRef<boolean>(false);

  const { state, dispatch } = useInputColorState({ value: currentValue });

  // If a new value is being pushed from the parent, and that value differs from
  // the current value of the reducer, then this is a programmatic update
  // and we need to call 'reset' to update the reducer state. Otherwise, the only
  // time the reducer state is updated from the value is on mount.
  if (colorsAreDifferent(lastValueFromParentRef.current, currentValue)) {
    lastValueFromParentRef.current = currentValue;
    if (colorsAreDifferent(currentValue, state.colorValue)) {
      dispatch({ type: 'reset', data: currentValue });
      lastColorRef.current = currentValue;
      lastCommittedColorRef.current = currentValue;
    }
  }

  // Reducer updates are async, so wait until the state updates and then call our callbacks.
  useEffect(() => {
    if (colorsAreDifferent(state.colorValue, lastColorRef.current)) {
      onInput({ previousValue: lastColorRef.current, value: state.colorValue });
      lastColorRef.current = state.colorValue;
    }
    if (
      colorSelectedRef.current &&
      colorsAreDifferent(state.colorValue, lastCommittedColorRef.current)
    ) {
      onCommit?.({ previousValue: lastCommittedColorRef.current, value: state.colorValue });
      lastCommittedColorRef.current = state.colorValue;
      colorSelectedRef.current = false;
    }
  }, [onCommit, onInput, state.colorValue]);

  // Handle onInput from InputColor as values are entered in the input field.
  // We wait for the state to update before calling onInput.
  const colorInputHandler = useCallback<InputColorProps['onInput']>(
    (detail: ValueUpdateDetail<ColorValue>) => {
      dispatch({ type: 'colorChanged', data: detail.value });
    },
    [dispatch]
  );

  // Handle onCommit from InputColor, which is called when the user presses Enter or the field is blurred.
  const colorCommitHandler = useCallback<NonNullable<InputColorProps['onCommit']>>(() => {
    // If the current color value differs from the last committed value, then call onCommit.
    if (colorsAreDifferent(lastCommittedColorRef.current, state.colorValue)) {
      onCommit?.({ value: state.colorValue, previousValue: lastCommittedColorRef.current });
      lastCommittedColorRef.current = state.colorValue;
    }
  }, [onCommit, state.colorValue]);

  // Respond to onAction event from the swatch icon button. Toggles the color picker dropdown.
  const colorIconActionHandler = useCallback<NonNullable<ColorSwatchProps['onToggle']>>(() => {
    // Only toggle the value of isOpen if we didn't just dismiss the dropdown.
    if (!outsideMousedownRef.current) {
      setIsOpen((prevIsOpen) => !prevIsOpen);
    }
    outsideMousedownRef.current = false;
  }, []);

  const dropdownCloseHandler = useCallback<NonNullable<DropdownProps['onClose']>>(
    (detail: CloseDetail) => {
      // Keep track of when the dropdown is closed via outside mousedown, including when the swatch icon
      // is clicked. We need this in order to toggle visibility of the picker and dropdown.
      if (detail.reason === 'outsideMousedown') {
        outsideMousedownRef.current = true;
        clearTimeout(outsideMousedownTimerRef.current);

        // Clear the flag after a short delay to ensure the dropdown can be opened again.
        outsideMousedownTimerRef.current = setTimeout(() => {
          outsideMousedownRef.current = false;
        }, 200);
      } else if (detail.reason === 'dismissed' || detail.reason === 'keyboardDismissed') {
        // Focus the swatch icon when the dropdown is closed.
        iconRef.current?.focus();
      }
      setIsOpen(false);
    },
    []
  );

  // Handle color selection in the picker.
  //   - Updates the value in state.
  //   - Closes the dropdown.
  //   - Sets focus to the color field.
  const colorPickerCommitHandler = useCallback<ColorPickerProps['onCommit']>(
    (detail: ValueUpdateDetail<ColorValue>) => {
      // Set focus to the swatch when a color is selected.
      iconRef.current?.focus();

      // Set a ref indicating a color was selected.
      colorSelectedRef.current = true;
      dispatch({ type: 'colorSelected', data: detail.value });
      setIsOpen(false);
    },
    [dispatch]
  );

  // The Redwood UX design calls for the dropdown to open when the down arrow is pressed.
  // Handle opening the dropdown when the down arrow is pressed in the text edit field.
  const colorPickerDownHandler = useCallback(() => {
    setIsOpen(true);
  }, []);

  const { triggerProps: containerDropdownProps } = useDownToggle({
    isOpen,
    onToggle: colorPickerDownHandler
  });

  return {
    colorIconActionHandler,
    colorPickerCommitHandler,
    dropdownCloseHandler,
    containerDropdownProps,
    colorValue: state.colorValue,
    iconRef,
    isOpen,
    colorCommitHandler,
    colorInputHandler,
    colorRef
  };
};

export { useInputColor };
